import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { JobSummaryFragment } from '@shared/generated/graphql';
import { useRelatedExternalMessages } from '@shared/plugin/pages/Home/components/email-thread-activity/hooks/useRelatedExternalMessages';
import { useThreadTrackedMovements } from '@shared/plugin/pages/Home/components/thread-tracking/useThreadTrackedMovements';
import { useMemo } from 'react';
import { FeedItem, SuggestedAction, SuggestedActionsFeedItem } from '../types';
import { combineItems } from '../utils/combineItems';
import { getEmptyStateItem } from '../utils/getEmptyStateItem';
import { transformRelatedExternalMessages } from '../utils/transformRelatedExternalMessages';
import { transformThreadJobs } from '../utils/transformThreadJobs';
import { transformTrackedMovements } from '../utils/transformTrackedMovements';
import { useFeedConversations } from './useFeedConversations';
import { useFeedQuoteEvents } from './useFeedQuoteEvents';
import { BuildJobHandler, useSuggestedActions } from './useSuggestedActions';

type FeedOptions = {
  handleSendToVooma: BuildJobHandler;
  disableTransactions: boolean;
};

type FeedState = {
  items: FeedItem[];
  suggestedActions: SuggestedActionsFeedItem[];
};

type FeedActions = {
  refresh: () => Promise<void>;
  executeAction: (action: SuggestedAction) => Promise<void>;
  getJobSuggestedActions: (job: JobSummaryFragment) => SuggestedAction[];
};

export type FeedValue = FeedState & FeedActions;

export const useFeed = (options: FeedOptions): FeedValue => {
  const {
    items: suggestedActionsItems,
    executeAction,
    getJobSuggestedActions,
  } = useSuggestedActions({
    handleSendToVooma: options.handleSendToVooma,
    disableTransactions: options.disableTransactions,
  });

  const { messages } = useRelatedExternalMessages();
  const messageClassificationFeedItems =
    transformRelatedExternalMessages(messages);

  const { threadJobs } = useEmailThreadContext();
  const jobCardFeedItems = transformThreadJobs(threadJobs);
  const { events: feedQuoteEvents } = useFeedQuoteEvents();
  const { conversations } = useFeedConversations();
  const { movements, refetch: refetchMovements } = useThreadTrackedMovements();

  const items = useMemo(() => {
    const trackedMovementFeedItems = transformTrackedMovements(
      movements,
      refetchMovements
    );
    return combineItems(
      ...messageClassificationFeedItems,
      ...jobCardFeedItems,
      ...feedQuoteEvents,
      ...conversations,
      ...trackedMovementFeedItems
    );
  }, [
    messageClassificationFeedItems,
    jobCardFeedItems,
    feedQuoteEvents,
    conversations,
    movements,
    refetchMovements,
  ]);

  return {
    items: items?.length ? items : [getEmptyStateItem()],
    suggestedActions: suggestedActionsItems,
    getJobSuggestedActions,
    executeAction,
    refresh: () => {
      throw new Error('refresh() not implemented');
    },
  };
};
